import './Page.css';

const plans = [
  {
    name: 'BASIC – 16 Hours',
    tagline: 'Build a Strong Foundation',
    price: '$750',
    period: '/ 16 Hours',
    desc: '8-week program · Weekly 2-hour Zoom sessions · Grades 6–9',
    features: ['Grammar & sentence structure', 'Vocabulary development', 'Reading fluency', 'Paragraph writing'],
  },
  {
    name: 'STANDARD – 24 Hours',
    tagline: 'Advance Reading & Writing',
    price: '$1,050',
    period: '/ 24 Hours',
    desc: '12-week program · Weekly or twice-weekly sessions · Grades 7–10',
    features: ['Everything in Basic Plan', 'Essay writing and structure', 'Textual & literary analysis', 'Nonfiction reading', 'Timed writing practice'],
    featured: true,
  },
  {
    name: 'ADVANCED – 32 Hours',
    tagline: 'College-Ready Writing',
    price: '$1,400',
    period: '/ 32 Hours',
    desc: '16-week program · 2–3 sessions/week (flexible) · Grades 8–12',
    features: ['Everything from Basic & Standard', 'Long-form evidence-based essays', 'Structured argumentation', 'Rhetorical analysis', 'College application writing'],
  },
];

const whyItems = [
  { icon: '🎯', title: 'Personalized Instruction', desc: 'Every session adapts to your child\'s goals and current skill level — not a one-size-fits-all curriculum.' },
  { icon: '📹', title: 'Live Zoom Sessions', desc: '1:1 instruction or small groups of 3–4 students. Real-time feedback from expert tutors.' },
  { icon: '📈', title: 'Progress-Based Approach', desc: 'Weekly check-ins track growth in writing fluency, grammar mastery, and reading analysis.' },
  { icon: '📝', title: 'Grammar, Reading & Writing', desc: 'Covers comprehension, sentence structure, timed writing, and analytical skills from the ground up.' },
  { icon: '🏆', title: 'SAT/ACT Performance', desc: 'Students improve school essays and standardized test scores through focused practice and feedback.' },
  { icon: '🎓', title: 'College Readiness', desc: 'Advanced students build personal statements, supplementals, and argumentation skills for admissions.' },
];

const testimonials = [
  { quote: 'My son was really struggling in middle school. The Basic ELA Plan helped rebuild his foundation and, more importantly, his confidence. Now he doesn\'t dread English assignments.', name: '— Fatima A., Parent of 7th Grader' },
  { quote: 'I used to write all my essays the night before. The Standard Plan helped me plan, organize, and actually understand what I was writing. School is so much less stressful now.', name: '— Jalen M, 10th Grade Student' },
  { quote: 'We enrolled my son in the Advanced Plan during his junior year for college applications. Not only did he improve his essay writing, but the college prep sessions were a game changer.', name: '— Smita Rao, Parent of 11th Grader' },
];

const faq = [
  { q: 'How do I register?', a: 'Click "Enroll" on any plan to be directed to our secure payment portal.' },
  { q: 'Who creates the account?', a: 'A parent or guardian should create the account and add the child as the student.' },
  { q: 'Do I need to verify my email?', a: 'Yes — check your inbox for a verification email to activate your profile.' },
  { q: 'Can the student sign up directly without a parent?', a: 'No. For students under 18, a parent or guardian must be the primary account holder.' },
  { q: 'What happens after I purchase a package?', a: 'You\'ll receive a confirmation and your credits will be applied. You\'ll be matched with a tutor and your schedule will be shared via email.' },
];

export default function ELAPage() {
  return (
    <div className="page-wrap">

      {/* Hero */}
      <div className="sat-hero">
        <div className="container">
          <div className="exam-hero-badge">ELA Mastery Program</div>
          <h1>English skills that last<br />beyond the classroom.</h1>
          <p className="sat-hero-sub">
            Live tutoring for Grades 5–12. Grammar, reading, essay writing,
            and college prep — built around your student's goals.
          </p>
          <p className="sat-hero-proof">★★★★★ &nbsp; Grades 5–12 · Live Zoom · 1:1 & Small Group</p>
          <div className="sat-hero-btns">
            <a href="/signup" target="_blank" rel="noreferrer" className="btn-gold">Enroll Now</a>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-outline-white">Book a Free Consultation →</a>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">3</div>
              <div className="exam-stat-label">Structured Plans</div>
            </div>
            <div>
              <div className="exam-stat-num">16–32</div>
              <div className="exam-stat-label">Hours of Instruction</div>
            </div>
            <div>
              <div className="exam-stat-num">5–12</div>
              <div className="exam-stat-label">Grade Levels Served</div>
            </div>
            <div>
              <div className="exam-stat-num">Live</div>
              <div className="exam-stat-label">Zoom Sessions</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* Why Scholar Sphere */}
        <div className="page-section">
          <h2 className="section-heading">Why Scholar Sphere for ELA?</h2>
          <p className="section-sub">Real skill-building — not generic homework help.</p>
          <div className="why-boxes">
            {whyItems.map(w => (
              <div className="why-box" key={w.title}>
                <div className="why-box-icon">{w.icon}</div>
                <div className="why-box-title">{w.title}</div>
                <div className="why-box-desc">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="page-section" id="plans">
          <h2 className="section-heading">Compare ELA Plans & Pricing</h2>
          <p className="section-sub">Three structured packages based on grade level and goals.</p>
          <div className="pricing-cards">
            {plans.map(p => (
              <div className={`pricing-card${p.featured ? ' featured' : ''}`} key={p.name}>
                <div className="pricing-plan-name">{p.name}</div>
                <p style={{ fontSize: 13, color: 'var(--text-gray)', marginBottom: 8 }}>{p.tagline}</p>
                <div className="pricing-price">{p.price}</div>
                <div className="pricing-period">{p.period}</div>
                <p className="pricing-desc">{p.desc}</p>
                <ul className="pricing-features">
                  {p.features.map(f => <li key={f}>{f}</li>)}
                </ul>
                <a href="/signup" target="_blank" rel="noreferrer" className="btn-gold" style={{ display: 'block', textAlign: 'center' }}>Get Started</a>
              </div>
            ))}
          </div>
          <p className="page-note">📌 Register using a parent account and add your student profile.</p>
        </div>

        {/* Testimonials */}
        <div className="page-section">
          <h2 className="section-heading">What Parents & Students Say</h2>
          <div className="mini-test-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {testimonials.map((t, i) => (
              <div className="mini-test-card" key={i}>
                <p className="mini-test-quote">&ldquo;{t.quote}&rdquo;</p>
                <div className="mini-test-name">{t.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="page-section">
          <h2 className="section-heading">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faq.map((item, i) => (
              <details key={i} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="cta-strip" style={{marginBottom: 32}}>
          <h3>Ready to Get Started?</h3>
          <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
        </div>
      </div>
    </div>
  );
}
